<template>
  <v-container fluid class="list-page">
    <Create :createDialog="createDialog" @close="createDialog = false" @submit="getList"></Create>
    <Edit :editDialog="editDialog" @close="editDialog = false" @submit="getList" :editDonor="selectedDonor"></Edit>
    <div class="list-page-heading">
      <div>
        <div class="list-page-heading__eyebrow">Community</div>
        <h1>Donors</h1>
        <p>Manage donor profiles, contact details, payment methods, and balances.</p>
      </div>

      <div class="list-page-heading__actions">
        <v-btn
          v-if="checkRightStatus(1)"
          color="primary"
          depressed
          @click="add"
        >
          <v-icon left>mdi-plus</v-icon>
          New donor
        </v-btn>
      </div>
    </div>

    <v-row class="list-metrics">
      <v-col cols="6" sm="4" md="3">
        <div class="list-metric">
          <div class="list-metric__icon">
            <v-icon color="primary" size="21">mdi-hand-coin-outline</v-icon>
          </div>
          <div>
            <div class="list-metric__value">{{ donors.length }}</div>
            <div class="list-metric__label">Total donors</div>
          </div>
        </div>
      </v-col>

      <v-col cols="6" sm="4" md="3">
        <div class="list-metric">
          <div class="list-metric__icon">
            <v-icon color="primary" size="21">mdi-cash-clock</v-icon>
          </div>
          <div>
            <div class="list-metric__value">{{ fullTotal | currency }}</div>
            <div class="list-metric__label">Outstanding balance</div>
          </div>
        </div>
      </v-col>

      <v-col cols="6" sm="4" md="3">
        <div class="list-metric">
          <div class="list-metric__icon">
            <v-icon color="primary" size="21">mdi-credit-card-check-outline</v-icon>
          </div>
          <div>
            <div class="list-metric__value">{{ onlinePaymentCount }}</div>
            <div class="list-metric__label">Online payment enabled</div>
          </div>
        </div>
      </v-col>

      <v-col cols="6" sm="4" md="3">
        <div class="list-metric">
          <div class="list-metric__icon">
            <v-icon color="primary" size="21">mdi-account-cash-outline</v-icon>
          </div>
          <div>
            <div class="list-metric__value">{{ balanceDonorCount }}</div>
            <div class="list-metric__label">Donors with balance</div>
          </div>
        </div>
      </v-col>
    </v-row>

    <v-card :loading="loading" class="list-card" elevation="0">
      <div class="list-toolbar">
        <v-text-field
          v-model="search"
          class="list-toolbar__search"
          outlined
          prepend-inner-icon="mdi-magnify"
          dense
          background-color="#F8FAF8"
          placeholder="Search by name, phone, or email"
          single-line
          hide-details
          clearable
        />

        <div class="list-toolbar__actions">
          <v-switch
            v-model="ShowBalanceOnly"
            color="primary"
            inset
            hide-details
            label="Balance only"
          />

          <v-btn
            icon
            outlined
            color="primary"
            :loading="loading"
            aria-label="Refresh donors"
            @click="getList"
          >
            <v-icon size="19">mdi-refresh</v-icon>
          </v-btn>
        </div>
      </div>

      <v-card-text class="pa-0">
        <v-data-table class="list-table donor-table" :headers="headers" :items="donors" :search="search" :loading="loading" :sortDesc="sortDesc"
          :sort-by="sortBy" :page.sync="page" :items-per-page="itemsPerPage">
          <template v-slot:item="{ item }">
            <tr class="list-table-row pointer">
              <td class="text-left" data-label="Created">
                <div class="list-cell-primary">{{ item.createdAt | moment("M/D/YYYY") }}</div>
                <div class="list-cell-secondary">{{ item.createdAt | moment("from", "now") }}</div>
              </td>
              <td class="text-left donor-name" @click="viewProfileNewTab(item)" 
                v-html="$options.filters.highlight(item.name, search.toLowerCase())">
                {{ item.name }}
              </td>
              <td>
                <div class="payment-indicators">
          
                <v-tooltip bottom v-if="item.stripeId">
                  <template v-slot:activator="{ on, attrs }">
                    <v-avatar size="10" color="blue" v-bind="attrs" v-on="on" />
                  </template>
                  <span>Paying With Stripe</span>
                </v-tooltip>

                <v-tooltip bottom v-if="item.payCompassId">
                  <template v-slot:activator="{ on, attrs }">
                    <v-avatar size="10" color="yellow" v-bind="attrs" v-on="on" />
                  </template>
                  <span>Set for online payment</span>
                </v-tooltip>
                </div>
              </td>
              <td class="text-left donor-contact" @click="viewProfile(item)" v-html="$options.filters.highlight(
                item.phoneNumber,
                search.toLowerCase()
              )
                ">
                {{ item.phoneNumber }}
              </td>
              <td class="text-left donor-contact" @click="viewProfile(item)" v-html="$options.filters.highlight(item.email, search.toLowerCase())
                ">
                {{ item.email }}
              </td>

              <!-- <td
                class="text-left"
                @click="viewProfile(item)"
                v-html="
                  $options.filters.highlight(item.address, search.toLowerCase())
                "
              >
                {{ item.address }}
              </td> -->
              <!-- <td
                class="text-left"
                @click="viewProfile(item)"
                v-html="
                  $options.filters.highlight(item.city, search.toLowerCase())
                "
              >
                {{ item.city }}
              </td> -->

              <td class="text-right donor-balance" :class="{ 'donor-balance--due': Number(item.balance) > 0 }" @click="viewProfile(item)">
                <span>{{
                  item.balance | currency
                  }}</span>
              </td>

              <td class="justify-end align-center text-right">
                <v-menu bottom left offset-y>
                  <template v-slot:activator="{ on }">
                    <v-btn v-on="on" icon>
                      <v-icon>mdi-dots-horizontal</v-icon>
                    </v-btn>
                  </template>
                  <v-list class="menu-button">
                    <v-list-item @click="viewProfile(item)">
                      <v-list-item-title>Profile</v-list-item-title>
                    </v-list-item>
                    <v-divider></v-divider>
                    <v-list-item v-if="checkRightStatus(3)" @click="editDonor(item)">
                      <v-list-item-title>{{
                        $t("buttons.edit")
                        }}</v-list-item-title>
                    </v-list-item>
                    <DeleteDonor v-if="checkRightStatus(4)" :donor="item" @submit="getList()" />
                  </v-list>
                </v-menu>
              </td>
            </tr>
          </template>
          <template v-slot:no-data>
            <div class="list-empty-state">
              <v-icon size="42" color="grey lighten-1">mdi-hand-coin-outline</v-icon>
              <strong>No donors found</strong>
              <span>Add your first donor to begin tracking donations and balances.</span>
              <v-btn
                v-if="checkRightStatus(1)"
                color="primary"
                depressed
                small
                @click="add"
              >
                <v-icon left small>mdi-plus</v-icon>
                Add donor
              </v-btn>
            </div>
          </template>

          <template v-slot:no-results>
            <div class="list-empty-state">
              <v-icon size="42" color="grey lighten-1">mdi-magnify-close</v-icon>
              <strong>No matching donors</strong>
              <span>No donors matched "{{ search }}".</span>
              <v-btn text color="primary" small @click="search = ''">
                Clear search
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import donorService from "../service.js";
import DeleteDonor from "@/modules/Donors/components/DeleteDonor.vue";

const Create = () => import("./Create");
const Edit = () => import("./Edit");
export default {
  data() {
    return {
      ShowBalanceOnly: false,
      createDialog: false,
      editDialog: false,
      selectedDonor: null,
      fullTotal: 0,
      headers: [
        {
          text: "Created",
          align: "left",
          value: "createdAt",
          sortable: true,
        },
        {
          text: "Name",
          value: "name",
          sortable: true,
          align: "left",
        },
        {
          text: "",
          value: "action",
          sortable: false,
        },
        {
          text: "Phone Number",
          value: "phoneNumber",
          align: "left",
          sortable: true,
        },
        {
          text: "Email Address",
          value: "email",
          align: "left",
          sortable: true,
        },
        // {
        //   text: "Address",
        //   value: "address",
        //   align: "left",
        //   sortable: true,
        // },
        {
          text: this.$t("labels.balance"),
          value: "balance",
          align: "left",
          sortable: true,
        },
        {
          text: "",
          value: "action",
          sortable: false,
        },
      ],
      page: 1,
      sortBy: "createdAt",
      pageCount: 0,
      sortDesc: true,
      itemsPerPage: 10,
      search: "",
      refundDialog: false,
      selectedTask: null,
      donors: [],
      loading: true,
    };
  },
  async created() {
    if (this.$route.query && this.$route.query.ShowBalanceOnly) {
      this.ShowBalanceOnly = this.$route.query.ShowBalanceOnly;
    }
    this.getList();
    // this.$events.listen("DonorListEvent", () => this.getList());

    // this.reset();
  },
  // beforeDestroy() {
  //   this.$events.remove("DonorListEvent");
  // },
  computed: {
    ...mapGetters("global", ["currentUser", "checkRightStatus"]),

    onlinePaymentCount() {
      return this.donors.filter((donor) => donor.stripeId || donor.payCompassId)
        .length;
    },

    balanceDonorCount() {
      return this.donors.filter((donor) => Number(donor.balance || 0) > 0)
        .length;
    },
  },
  watch: {
    ShowBalanceOnly() {
      this.getList();
    },
    search(val) {
      if (!val) {
        this.search = "";
      }
    },
  },
  components: {
    Create,
    Edit,
    DeleteDonor,
  },
  filters: {
    highlight(words, query) {
      if (query) {
        var check = new RegExp(query.toLowerCase(), "ig");
        if (words) {
          return words.toString().replace(check, function (matchedText) {
            return '<span class="highlight">' + matchedText + "</span>";
          });
        } else {
          return words;
        }
      } else {
        return words;
      }
    },
  },
  methods: {
    viewProfile(val) {
      //  let routeData = this.$router.resolve({
      //   path: `/parents/${val.id}/profile`,
      // });
      // window.open(routeData.href, "_blank");
      this.$router.push({ path: `/donors/${val.id}/profile` });
    },
    viewProfileNewTab(val) {
      let routeData = this.$router.resolve({
        path: `/donors/${val.id}/profile`,
      });
      window.open(routeData.href, "_blank");
    },
    add() {
      this.createDialog = !this.createDialog;
    },
    getList() {
      this.loading = true;
      let where = {};
      if (this.ShowBalanceOnly) {
        where.hideZero = true;
      }
      return donorService
        .getAll(where)
        .then((response) => {
          this.donors = response.data || [];
          this.fullTotal = this.donors.reduce((total, row) => total + Number(row.balance || 0), 0);
          this.loading = false;
          return response;
        })
        .catch((error) => {
          throw error;
        });
    },
    editDonor(donor) {
      this.selectedDonor = donor;
      this.editDialog = true;
    },
    deleteDonor(donor) {
      if (!this.checkRightStatus(39)) {
        this.$swal(
          "Delete donor",
          "You are not allow to delete donor with balance.",
          "warning"
        );
        return false;
      }
      this.$swal({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#B71C1C",
        cancelButtonColor: "#43A047",
        confirmButtonText: "Yes",
        cancelButtonText: "No",
      }).then((result) => {
        if (result.value) {
          donorService
            .delete(donor.id)
            .then((response) => {
              if (response.data) {
                this.$swal("Deleted!", "Donor has been deleted.", "success");
                this.getList();
              }
            })
            .catch((error) => {
              throw error;
            });
        }
      });
    },
  },
};
</script>

<style scoped>
/* Shared page, heading, metrics, card, toolbar, search, table,
   and empty-state styles come from app.scss. */

.list-toolbar__actions ::v-deep .v-input--switch {
  margin: 0;
  padding: 0;
}

.donor-name {
  color: #2e7d32;
  font-weight: 650;
  text-decoration: none;
  cursor: pointer;
}

.donor-contact {
  color: #445047;
  font-size: 12px;
}

.donor-balance {
  color: #253047;
  font-weight: 700;
}

.donor-balance--due {
  color: #2e7d32;
}

.payment-indicators {
  display: flex;
  align-items: center;
  gap: 7px;
}

@media (max-width: 1100px) {
  .donor-table {
    overflow-x: auto;
  }

  .donor-table ::v-deep table {
    min-width: 900px;
  }
}

@media (max-width: 599px) {
  .list-toolbar__actions {
    justify-content: space-between;
  }

  .list-toolbar__actions ::v-deep .v-label {
    font-size: 13px;
  }
}
</style>
